import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import {
  FaSearch,
  FaSyncAlt,
  FaTimesCircle,
  FaWhatsapp,
} from "react-icons/fa";
import { Gem } from "lucide-react";

import {
  useGetWhatsAppCatalogItemsQuery,
  useSyncLabelledStockToWhatsAppCatalogMutation,
  useUpdateWhatsAppCatalogPublishStatusMutation,
} from "../toolkitServices/whatsappApi";

const Spinner = ({ size = 15, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

const inputClass =
  "rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] text-gray-800 focus:outline-none focus:ring-1 focus:ring-violet-400";

const getSelectedBranch = () => {
  try {
    const raw =
      localStorage.getItem("selectedBranch") ||
      localStorage.getItem("branch") ||
      localStorage.getItem("currentBranch");

    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const EmptyState = ({ title, desc }) => (
  <div className="flex min-h-[220px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white p-5 text-center">
    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-50 text-violet-700">
      <Gem size={21} />
    </div>
    <h3 className="mt-3 text-sm font-bold text-slate-800">{title}</h3>
    <p className="mt-1 max-w-sm text-xs text-slate-500">{desc}</p>
  </div>
);

const WhatsAppCatalog = () => {
  const selectedBranch = getSelectedBranch();

  const branchId =
    selectedBranch?._id || selectedBranch?.id || selectedBranch?.branchId || "";

  const branchName =
    selectedBranch?.branchName ||
    selectedBranch?.name ||
    selectedBranch?.BranchName ||
    "";

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const { data: catalogData, isFetching } = useGetWhatsAppCatalogItemsQuery(
    branchId ? { branchId } : {},
  );

  const [syncCatalog, { isLoading: syncing }] =
    useSyncLabelledStockToWhatsAppCatalogMutation();

  const [updatePublishStatus, { isLoading: publishUpdating }] =
    useUpdateWhatsAppCatalogPublishStatusMutation();

  const items = catalogData?.data || [];

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();

    return items.filter((item) => {
      const matchesSearch =
        !q ||
        item.name?.toLowerCase().includes(q) ||
        item.itemCode?.toLowerCase().includes(q) ||
        item.orderCode?.toLowerCase().includes(q) ||
        item.categoryName?.toLowerCase().includes(q) ||
        item.productName?.toLowerCase().includes(q);

      const matchesStatus =
        !statusFilter ||
        (statusFilter === "published" && item.isWhatsappPublished) ||
        (statusFilter === "hidden" && !item.isWhatsappPublished);

      return matchesSearch && matchesStatus;
    });
  }, [items, search, statusFilter]);

  const publishedCount = items.filter((x) => x.isWhatsappPublished).length;
  const hiddenCount = items.length - publishedCount;
  const selectedCount = selectedIds.length;
  const hasFilters = search || statusFilter;

  useEffect(() => {
    setSelectedIds([]);
  }, [branchId]);

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleSelectAll = () => {
    const ids = filteredItems.map((x) => x._id);
    const allSelected =
      ids.length > 0 && ids.every((id) => selectedIds.includes(id));

    setSelectedIds((prev) =>
      allSelected
        ? prev.filter((id) => !ids.includes(id))
        : Array.from(new Set([...prev, ...ids])),
    );
  };

  const handleSync = async () => {
    try {
      await syncCatalog(branchId ? { branchId } : {}).unwrap();
      setSelectedIds([]);
      toast.success("Catalog synced successfully");
    } catch (err) {
      toast.error(err?.data?.message || "Catalog sync failed");
    }
  };

  const handlePublishToggle = async (item) => {
    try {
      const nextStatus = !item.isWhatsappPublished;

      await updatePublishStatus({
        id: item._id,
        isWhatsappPublished: nextStatus,
      }).unwrap();

      toast.success(nextStatus ? "Item published" : "Item hidden");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update status");
    }
  };

  const handleBulkPublish = async (status) => {
    if (!selectedIds.length) {
      toast.error("Please select items first");
      return;
    }

    try {
      await Promise.all(
        selectedIds.map((id) =>
          updatePublishStatus({
            id,
            isWhatsappPublished: status,
          }).unwrap(),
        ),
      );

      setSelectedIds([]);
      toast.success(
        status ? "Selected items published" : "Selected items hidden",
      );
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update selected items");
    }
  };

  return (
    <div className="relative flex h-[calc(100vh-140px)] flex-col overflow-hidden rounded-2xl bg-white shadow-lg md:h-[calc(100vh-70px)]">
      {isFetching && !items.length && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/60">
          <div className="flex flex-col items-center gap-2 text-xs text-[#6b59c6]">
            <Spinner size={26} />
            <span>Loading catalog…</span>
          </div>
        </div>
      )}

      {/* Compact Header */}
      <div className="shrink-0 rounded-t-xl bg-[#9481ce] text-white">
        <div className="flex items-center justify-between gap-3 px-4 py-1.5">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <FaWhatsapp className="shrink-0 text-base" />

              <h2 className="truncate text-[16px] font-semibold tracking-tight">
                WhatsApp Catalog
              </h2>

              {isFetching && items.length > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-[10px] text-white">
                  <Spinner size={9} />
                  refreshing…
                </span>
              )}
            </div>

            <p className="truncate text-[10px] text-violet-100">
              {branchName || "Publish jewellery items to WhatsApp"}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <span className="hidden rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-semibold sm:inline-flex">
              Total: {items.length}
            </span>

            <span className="hidden rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold text-emerald-700 sm:inline-flex">
              Published: {publishedCount}
            </span>

            <span className="hidden rounded-full bg-rose-50 px-2.5 py-1 text-[10px] font-semibold text-rose-700 md:inline-flex">
              Hidden: {hiddenCount}
            </span>

            <button
              type="button"
              onClick={handleSync}
              disabled={syncing}
              className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-[11px] font-semibold text-[#6b59c6] shadow-sm transition hover:bg-violet-50 disabled:opacity-60"
            >
              {syncing ? <Spinner size={11} /> : <FaSyncAlt size={11} />}
              Sync
            </button>
          </div>
        </div>
      </div>

      {/* Compact Filters */}
      <div className="shrink-0 border-b bg-slate-50/80 px-4 py-1.5">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex w-[125px] flex-col gap-[1px]">
            <label className="text-[10px] font-medium text-slate-600">
              Status
            </label>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`${inputClass} h-7 py-1 text-[10px]`}
            >
              <option value="">All</option>
              <option value="published">Published</option>
              <option value="hidden">Hidden</option>
            </select>
          </div>

          <div className="flex min-w-[170px] flex-1 flex-col gap-[1px]">
            <label className="text-[10px] font-medium text-slate-600">
              Search
            </label>

            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400" />

              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`${inputClass} h-7 w-full py-1 pl-8 text-[10px]`}
                placeholder="Item code, name, category…"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 pb-[1px]">
            <span className="inline-flex rounded-full bg-violet-50 px-2.5 py-1 text-[10px] font-semibold text-violet-700 sm:hidden">
              Total: {items.length}
            </span>

            <span className="rounded-full bg-violet-50 px-2.5 py-1 text-[10px] font-semibold text-violet-700">
              Selected: {selectedCount}
            </span>

            <button
              type="button"
              onClick={toggleSelectAll}
              disabled={!filteredItems.length}
              className="inline-flex h-7 items-center rounded-full bg-white px-2.5 text-[10px] font-semibold text-[#6b59c6] ring-1 ring-violet-200 hover:bg-violet-50 disabled:opacity-40"
            >
              Select All
            </button>

            <button
              type="button"
              disabled={!selectedCount || publishUpdating}
              onClick={() => handleBulkPublish(true)}
              className="h-7 rounded-full bg-emerald-600 px-2.5 text-[10px] font-semibold text-white disabled:opacity-40"
            >
              Publish
            </button>

            <button
              type="button"
              disabled={!selectedCount || publishUpdating}
              onClick={() => handleBulkPublish(false)}
              className="h-7 rounded-full bg-rose-600 px-2.5 text-[10px] font-semibold text-white disabled:opacity-40"
            >
              Hide
            </button>

            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex h-7 items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 text-[10px] font-semibold text-slate-600 hover:bg-slate-50"
              >
                <FaTimesCircle size={9} />
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="min-h-0 flex-1 overflow-y-auto p-2.5 sm:p-3">
        {!items.length ? (
          <EmptyState
            title="No catalog items"
            desc="Click Sync to create WhatsApp catalog from labelled stock."
          />
        ) : !filteredItems.length ? (
          <EmptyState
            title="No matching items"
            desc="Try clearing filters or search with item code."
          />
        ) : (
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredItems.map((item, index) => {
              const checked = selectedIds.includes(item._id);
              const published = !!item.isWhatsappPublished;

              return (
                <motion.div
                  key={item._id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.18, delay: index * 0.01 }}
                  className={`overflow-hidden rounded-2xl border bg-white shadow-sm transition hover:shadow-md ${
                    checked
                      ? "border-violet-400 ring-2 ring-violet-100"
                      : "border-slate-200"
                  }`}
                >
                  <div className="relative aspect-[4/3] bg-slate-100">
                    <label className="absolute right-2 top-2 z-10 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-200">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSelect(item._id)}
                        className="h-4 w-4 cursor-pointer accent-violet-600"
                      />
                    </label>

                    <span
                      className={`absolute left-2 top-2 z-10 rounded-full px-2 py-1 text-[9px] font-bold ${
                        published
                          ? "bg-emerald-500 text-white"
                          : "bg-slate-700 text-white"
                      }`}
                    >
                      {published ? "LIVE" : "HIDDEN"}
                    </span>

                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={item.name || item.itemCode}
                        className={`h-full w-full object-cover ${
                          published ? "" : "grayscale"
                        }`}
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-slate-400">
                        <Gem size={24} />
                      </div>
                    )}
                  </div>

                  <div className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="line-clamp-1 text-sm font-bold text-slate-800">
                          {item.name || item.itemCode || "-"}
                        </p>

                        <p className="mt-0.5 truncate text-[11px] text-slate-500">
                          {item.itemCode || "-"} · {item.netWt || 0}g
                        </p>
                      </div>

                      <p className="shrink-0 text-sm font-bold text-violet-700">
                        ₹{item.price || 0}
                      </p>
                    </div>

                    <div className="mt-2 flex items-center justify-between gap-2 rounded-xl bg-slate-50 px-3 py-2">
                      <p className="truncate text-[11px] font-semibold text-slate-600">
                        {item.orderCode || "-"}
                      </p>

                      <button
                        type="button"
                        disabled={publishUpdating}
                        onClick={() => handlePublishToggle(item)}
                        className={`shrink-0 rounded-lg px-2.5 py-1 text-[10px] font-bold disabled:opacity-60 ${
                          published
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-violet-50 text-violet-700"
                        }`}
                      >
                        {published ? "Hide" : "Publish"}
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppCatalog;